/**
 * Chế độ ẨN DANH cho link chia sẻ kết quả: RIS thêm `&anDanh=1` (hoặc đơn vị đặt `anDanh: true` trong app-config).
 * Không hiện họ tên, mã bệnh nhân, ngày sinh; giữ tuổi và giới. Tắt bảng thẻ DICOM, các đường tải / chép tệp (CSV số
 * đo, DICOM SEG / RTSS / SR), danh sách ca, và không nạp SEG / RTSTRUCT / bản đồ tham số (phải tải nguyên tệp DICOM,
 * máy chủ từ chối với link ẩn danh).
 *
 * Tham số URL chỉ BẬT được, không tắt được cấu hình. Chỉ che phần giao diện — không thay cho việc chặn ở máy chủ.
 */
let daChot: boolean | undefined;
let timKiemChot: string | undefined;

export function docCheDoAnDanh(cauHinh?: { anDanh?: unknown }, timKiem = ''): boolean {
  return new URLSearchParams(timKiem).get('anDanh') === '1' || cauHinh?.anDanh === true;
}

function timKiemTrang(): string {
  return typeof window === 'undefined' ? '' : window.location.search;
}

/**
 * appInit gọi một lần khi đã có appConfig thật (app-config dạng hàm, configUrl…), trước khi nạp extension / mode. Từ đó
 * mọi nơi đọc cùng một kết quả, kể cả khi địa chỉ trang đổi sau này.
 */
export function chotCheDoAnDanh(appConfig?: { anDanh?: unknown }): boolean {
  timKiemChot = timKiemTrang();
  daChot = docCheDoAnDanh(appConfig, timKiemChot);
  return daChot;
}

export function laCheDoAnDanh(): boolean {
  if (daChot !== undefined) {
    return daChot;
  }
  // Chưa qua appInit (test, mã chạy sớm): đọc thẳng window.config khi nó là đối tượng.
  const cauHinh = typeof window === 'undefined' ? undefined : (window as any).config;
  return docCheDoAnDanh(
    cauHinh && typeof cauHinh === 'object' ? cauHinh : undefined,
    timKiemTrang()
  );
}

/**
 * Ảnh hay IN tên / mã / ngày sinh vào điểm ảnh — bỏ chữ 4 góc không che được — nên link ẩn danh bỏ, ở MỌI study: ảnh chụp
 * màn hình (Secondary Capture, kể cả màn liều chiếu CT mang modality CT) và ảnh có ImageType chứa SCREEN SAVE. Study PHỤ
 * của ca (ảnh dựng lại của trạm hậu xử lý; RIS xếp study chính ĐẦU TIÊN trong StudyInstanceUIDs) bỏ thêm ảnh tự khai
 * BurnedInAnnotation = YES và chỉ hiện loại máy trong danh sách RIS truyền qua `anDanhLoai` (đúng danh sách Ảnh chính,
 * Support.Class.AnhChinh phía RIS); thiếu danh sách thì ẩn hết study phụ. Study chính GIỮ ảnh BurnedInAnnotation = YES
 * (siêu âm, X-quang dấu L/R) — user chốt 09/10/2026: bệnh nhân phải còn thấy ảnh của mình. Chỉ chống HIỂN THỊ: metadata
 * vẫn tải được bằng giấy phép.
 */
// Phải khớp AnhChinh.LopChupManHinh phía RIS (luật Ảnh chính): lớp này và mọi lớp con `.7.x`.
const LOP_CHUP_MAN_HINH = '1.2.840.10008.5.1.4.1.1.7';

export function docStudyPhuAnDanh(timKiem: string): { studyChinh: string; loaiHien: Set<string> } {
  const p = new URLSearchParams(timKiem);
  const dsUid = [...p.getAll('StudyInstanceUIDs'), ...p.getAll('studyInstanceUIDs')].join(',');
  const loai = (p.get('anDanhLoai') ?? '').split(',').map(s => s.trim().toUpperCase());
  return {
    studyChinh: dsUid.split(',').map(s => s.trim()).find(Boolean) ?? '',
    loaiHien: new Set(loai.filter(Boolean)),
  };
}

let docGanNhat: { timKiem: string; kq: ReturnType<typeof docStudyPhuAnDanh> } | undefined;

function docStudyPhuNho(timKiem: string) {
  if (docGanNhat?.timKiem !== timKiem) {
    docGanNhat = { timKiem, kq: docStudyPhuAnDanh(timKiem) };
  }
  return docGanNhat.kq;
}

function chuoiDon(giaTri: unknown): string {
  return typeof giaTri === 'string' ? giaTri.trim().toUpperCase() : '';
}

/** Mọi giá trị của thẻ nhiều giá trị: dcmjs để mảng, chuỗi một giá trị vẫn có thể mang dấu `\`. */
function cacGiaTri(giaTri: unknown): string[] {
  const ds = Array.isArray(giaTri) ? giaTri : typeof giaTri === 'string' ? giaTri.split('\\') : [];
  return ds.map(chuoiDon);
}

function laChupManHinh(doiTuong: { SOPClassUID?: unknown; ImageType?: unknown }) {
  const lop = chuoiDon(doiTuong.SOPClassUID);
  return (
    lop === LOP_CHUP_MAN_HINH ||
    lop.startsWith(LOP_CHUP_MAN_HINH + '.') ||
    cacGiaTri(doiTuong.ImageType).includes('SCREEN SAVE')
  );
}

export function anKhiAnDanh(
  studyInstanceUID: string,
  doiTuong: { Modality?: unknown; SOPClassUID?: unknown; BurnedInAnnotation?: unknown; ImageType?: unknown },
  anDanh = laCheDoAnDanh(),
  timKiem = timKiemChot ?? timKiemTrang()
): boolean {
  if (!anDanh) {
    return false;
  }
  if (laChupManHinh(doiTuong)) {
    return true;
  }
  const { studyChinh, loaiHien } = docStudyPhuNho(timKiem);
  if (!studyChinh || studyInstanceUID === studyChinh) {
    return false;
  }
  if (cacGiaTri(doiTuong.BurnedInAnnotation).includes('YES')) {
    return true;
  }
  // Modality nhiều giá trị (tệp sai chuẩn, dcmjs để nguyên mảng) coi như không rõ loại ⇒ ẩn.
  return !loaiHien.has(chuoiDon(doiTuong.Modality));
}

/** Lệnh bị chặn khi ẩn danh: báo cho người xem rồi trả true để lệnh dừng. */
export function chanKhiAnDanh(
  uiNotificationService: { show: (thongBao: Record<string, unknown>) => void },
  tieuDe: string,
  viec: string
): boolean {
  if (!laCheDoAnDanh()) {
    return false;
  }
  uiNotificationService.show({
    title: tieuDe,
    message: `Link chia sẻ ẩn danh không ${viec}`,
    type: 'warning',
  });
  return true;
}

export default laCheDoAnDanh;
